import { read } from "../net/rally.ts";

const UNDER = "rally:last-conversation:";

type Reach = {
  me: { account: string };
  personal: Array<{ id: string }>;
  projects: Array<{ conversations: Array<{ id: string }> }>;
};

type RestoreOptions = {
  origin?: string;
  storage?: Storage | null;
  reach?: () => Promise<Reach>;
};

function browserStorage(): Storage | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

function browserOrigin(): string {
  return typeof window === "undefined" ? "" : window.location.origin;
}

export function lastConversationKey(account: string, origin: string): string {
  return `${UNDER}${encodeURIComponent(origin)}:${encodeURIComponent(account)}`;
}

export function rememberLastConversation(
  account: string,
  conversation: string,
  origin = browserOrigin(),
  storage = browserStorage(),
): void {
  try {
    storage?.setItem(lastConversationKey(account, origin), conversation);
  } catch {}
}

function recalledConversation(account: string, origin: string, storage: Storage | null): string | null {
  try {
    return storage?.getItem(lastConversationKey(account, origin)) ?? null;
  } catch {
    return null;
  }
}

function forgetRecalled(account: string, origin: string, storage: Storage | null): void {
  try {
    storage?.removeItem(lastConversationKey(account, origin));
  } catch {}
}

export async function conversationToRestore(
  account: string,
  options: RestoreOptions = {},
): Promise<string | null> {
  const origin = options.origin ?? browserOrigin();
  const storage = options.storage === undefined ? browserStorage() : options.storage;
  const conversation = recalledConversation(account, origin, storage);
  if (!conversation) return null;

  try {
    const where = await (options.reach ?? (() => read<Reach>("where.i.am")))();
    if (where.me.account !== account) return null;
    const reachable = [
      ...where.personal.map((one) => one.id),
      ...where.projects.flatMap((project) => project.conversations.map((one) => one.id)),
    ].includes(conversation);
    if (reachable) return conversation;
    forgetRecalled(account, origin, storage);
  } catch {}
  return null;
}
